// src/components/PushPrimer.js
// Écran d'explication maison AVANT la demande de permission système (Android/iOS).
// On ne peut pas restyler la boîte de dialogue de l'OS : on prépare donc le terrain
// avec notre propre message, puis le bouton déclenche la vraie demande OS.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPushPermission, requestAndRegisterPush, isNativePush } from "../utils/push";

// Évite de re-proposer plusieurs fois dans la même session (navigation, re-render).
let shownThisSession = false;

export default function PushPrimer() {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            if (!isNativePush() || shownThisSession) return;
            const perm = await getPushPermission();
            if (!alive) return;
            if (perm === "prompt" || perm === "prompt-with-rationale") {
                shownThisSession = true;
                setVisible(true);
            }
        })();
        return () => { alive = false; };
    }, []);

    if (!visible) return null;

    const handleEnable = async () => {
        setBusy(true);
        try {
            await requestAndRegisterPush();
        } finally {
            setBusy(false);
            setVisible(false);
        }
    };

    const benefits = t("pushPrimer.benefits", { returnObjects: true });
    const benefitsList = Array.isArray(benefits) ? benefits : [];

    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10050, padding: 18 }}>
            <div style={{ background: "#fff", borderRadius: 18, padding: "26px 22px 20px", maxWidth: 340, width: "100%", textAlign: "center", boxShadow: "0 16px 48px rgba(0,0,0,0.28)" }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
                    <span style={{ width: 64, height: 64, borderRadius: "50%", background: "var(--primary-blue)", display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 16px rgba(0,119,182,0.35)" }}>
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                            <path d="M13.7 21a2 2 0 0 1-3.4 0" />
                        </svg>
                    </span>
                </div>

                <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.25rem", color: "#223344", margin: "0 0 8px" }}>
                    {t("pushPrimer.title")}
                </h2>
                <p style={{ color: "#556", fontSize: 14, lineHeight: 1.5, margin: "0 0 14px" }}>
                    {t("pushPrimer.body")}
                </p>

                {benefitsList.length > 0 && (
                    <ul style={{ listStyle: "none", padding: 0, margin: "0 0 18px", textAlign: "left", display: "flex", flexDirection: "column", gap: 8 }}>
                        {benefitsList.map((b, i) => (
                            <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", color: "#425a63", fontSize: 13.5, lineHeight: 1.4 }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-blue)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                )}

                <button
                    type="button"
                    onClick={handleEnable}
                    disabled={busy}
                    style={{ width: "100%", background: "var(--primary-blue)", color: "#fff", border: "none", borderRadius: 12, padding: "12px", fontWeight: 700, fontSize: 15, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}
                >
                    {busy ? t("pushPrimer.enabling") : t("pushPrimer.enable")}
                </button>
                <button
                    type="button"
                    onClick={() => setVisible(false)}
                    style={{ width: "100%", background: "none", color: "#8a97a3", border: "none", padding: "12px", fontWeight: 600, fontSize: 14, cursor: "pointer", marginTop: 2 }}
                >
                    {t("pushPrimer.later")}
                </button>
            </div>
        </div>
    );
}
